import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ChevronDown, Loader2, Crown, Check, EyeOff,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MyGroupsMenu — top-nav dropdown that doubles as the calendar
 * visibility selector (Feb 2026, per Steve).
 *
 * Layout:
 *   ┌─ Your groups · 4 ─────────────┐
 *   │ ☑ Select all                  │
 *   ├───────────────────────────────┤
 *   │ ☑ Family            [Owner]   │
 *   │ ☑ Work                        │
 *   │ ☐ Tennis            (hidden)  │  ← unchecked = personally hidden
 *   │ ☑ Cousins           [Owner]   │
 *   ├───────────────────────────────┤
 *   │ Manage all groups →           │
 *   └───────────────────────────────┘
 *
 * Toggling a checkbox PATCHes /api/auth/profile with the user's
 * `hidden_group_ids` array. Backend filters /api/events accordingly,
 * and the user's preference syncs across devices.
 *
 * When the owner sets a group to `hidden_for_members=true`, that group
 * is shown as "Hidden by owner" with the checkbox locked off — the
 * row is informational only for non-owners.
 *
 * If the parent passes `onChanged`, we call it after every toggle so
 * the Dashboard can re-fetch events without waiting for a refresh.
 */
export default function MyGroupsMenu({ currentUserId, onChanged }) {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [upcomingByGroup, setUpcomingByGroup] = useState({});
  const ref = useRef(null);

  const hiddenSet = useMemo(
    () => new Set(user?.hidden_group_ids || []),
    [user?.hidden_group_ids]
  );

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Re-fetch every time the menu opens so freshly-created groups
    // appear immediately. Also pull EVERY event (include_hidden=true)
    // so we can show an "N events this week" hint next to groups the
    // user has personally hidden — gentle nudge to re-enable when
    // something is coming up.
    setBusy(true);
    Promise.all([
      api.get("/groups"),
      api.get("/events", { params: { include_hidden: true } }),
    ])
      .then(([gRes, eRes]) => {
        setGroups(gRes.data || []);
        const today = new Date();
        const weekOut = new Date(today); weekOut.setDate(weekOut.getDate() + 7);
        const todayStr = today.toISOString().slice(0, 10);
        const weekStr = weekOut.toISOString().slice(0, 10);
        const counts = {};
        for (const ev of (eRes.data || [])) {
          if (!ev.date) continue;
          const d = ev.date.slice(0, 10);
          if (d >= todayStr && d <= weekStr) {
            counts[ev.group_id] = (counts[ev.group_id] || 0) + 1;
          }
        }
        setUpcomingByGroup(counts);
      })
      .catch((e) => console.warn("[my-groups] fetch failed:", e?.message || e))
      .finally(() => setBusy(false));
  }, [open]);

  const saveHidden = async (nextHidden) => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", { hidden_group_ids: Array.from(nextHidden) });
      await refresh();
      onChanged && onChanged();
      // Tell the rest of the app (specifically Dashboard) to reload
      // events so the calendar reflects the new visibility immediately.
      window.dispatchEvent(new CustomEvent("bd:visibility-changed"));
    } catch (e) {
      console.warn("[my-groups] save failed:", e?.message || e);
    } finally {
      setSaving(false);
    }
  };

  const toggleOne = (gid) => {
    const next = new Set(hiddenSet);
    if (next.has(gid)) next.delete(gid); else next.add(gid);
    saveHidden(next);
  };

  // Personally-toggleable groups (excludes owner-broadcast-hidden ones,
  // which the user can't override).
  const togglable = groups.filter(
    (g) => !(g.hidden_for_members && g.owner_id !== currentUserId)
  );
  const allOn = togglable.every((g) => !hiddenSet.has(g.group_id));
  const anyOn = togglable.some((g) => !hiddenSet.has(g.group_id));

  const selectAll = () => {
    if (allOn) {
      // Turn EVERY togglable group off (add to hidden set).
      const next = new Set(hiddenSet);
      togglable.forEach((g) => next.add(g.group_id));
      saveHidden(next);
    } else {
      // Turn every togglable group on (remove from hidden set).
      const next = new Set(hiddenSet);
      togglable.forEach((g) => next.delete(g.group_id));
      saveHidden(next);
    }
  };

  // Visible count for the Groups pill label.
  const visibleCount = togglable.filter((g) => !hiddenSet.has(g.group_id)).length;
  const totalCount = groups.length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bd-tab flex items-center gap-1"
        title="Your groups & calendar visibility"
        aria-label="Your groups and calendar visibility"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-my-groups"
      >
        <Users size={14} /> <span className="hidden sm:inline">Groups</span>
        {totalCount > 0 && (
          <span className="bd-mono text-[10px]" style={{ opacity: 0.7 }}>
            {visibleCount}/{totalCount}
          </span>
        )}
        <ChevronDown size={10} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
      </button>

      {open && (
        <div
          className="bd-surface mt-2"
          style={{
            // Fixed to the viewport so we control both edges directly
            // and the panel can never overflow the screen regardless of
            // where the trigger button sits in the nav.
            position: "fixed",
            top: "3.5rem",
            right: "0.5rem",
            width: "min(20rem, calc(100vw - 1rem))",
            borderRadius: "var(--radius-card)",
            boxShadow: "0 20px 50px -12px rgba(0,0,0,0.30)",
            padding: "0.4rem",
            zIndex: 60,
            maxHeight: "min(70vh, calc(100dvh - 5rem))",
            overflowY: "auto",
            background: "var(--bg-elev)",
          }}
          role="menu"
          data-testid="my-groups-panel"
        >
          <div
            className="px-3 py-2 text-xs uppercase tracking-widest flex items-center justify-between"
            style={{ color: "var(--text-dim)", borderBottom: "1px solid var(--border)" }}
          >
            <span>Your groups · calendar view</span>
            {saving && <Loader2 size={10} className="animate-spin" />}
          </div>

          {busy && (
            <div className="flex items-center gap-2 text-sm px-3 py-3" style={{ color: "var(--text-dim)" }}>
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          )}

          {!busy && groups.length === 0 && (
            <div className="text-sm px-3 py-3" style={{ color: "var(--text-dim)" }} data-testid="my-groups-empty">
              You aren&apos;t in any groups yet.
            </div>
          )}

          {!busy && togglable.length > 0 && (
            <button
              type="button"
              onClick={selectAll}
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-semibold"
              style={{ color: "var(--text)", borderBottom: "1px solid var(--border)" }}
              data-testid="my-groups-select-all"
            >
              <span
                className="w-5 h-5 grid place-items-center rounded border shrink-0"
                style={{
                  borderColor: "var(--border-strong)",
                  background: allOn ? "var(--primary)" : (anyOn ? "color-mix(in srgb, var(--primary) 35%, transparent)" : "transparent"),
                  color: allOn ? "var(--primary-fg)" : "var(--text)",
                }}
              >
                {allOn && <Check size={12} strokeWidth={3} />}
                {!allOn && anyOn && <Check size={10} strokeWidth={3} style={{ opacity: 0.65 }} />}
              </span>
              <span className="flex-1 text-left">
                {allOn ? "All groups on" : (anyOn ? "Some groups visible" : "All groups hidden")}
              </span>
              <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
                {allOn ? "Hide all" : "Show all"}
              </span>
            </button>
          )}

          {groups.map((g) => {
            const isOwner = currentUserId && g.owner_id === currentUserId;
            const broadcastHidden = !!g.hidden_for_members && !isOwner;
            const personallyHidden = hiddenSet.has(g.group_id);
            const visible = !broadcastHidden && !personallyHidden;
            const upcoming = upcomingByGroup[g.group_id] || 0;
            return (
              <div
                key={g.group_id}
                className="flex items-center gap-2 px-3 py-2 rounded text-sm"
                style={{ color: "var(--text)" }}
                data-testid={`my-groups-row-${g.group_id}`}
              >
                <button
                  type="button"
                  onClick={() => !broadcastHidden && toggleOne(g.group_id)}
                  disabled={broadcastHidden}
                  className="w-5 h-5 grid place-items-center rounded border shrink-0"
                  style={{
                    borderColor: "var(--border-strong)",
                    background: visible ? "var(--primary)" : "transparent",
                    color: visible ? "var(--primary-fg)" : "var(--text)",
                    cursor: broadcastHidden ? "not-allowed" : "pointer",
                    opacity: broadcastHidden ? 0.4 : 1,
                  }}
                  title={broadcastHidden ? "Hidden by group owner" : (visible ? "Hide from my calendar" : "Show on my calendar")}
                  data-testid={`my-groups-check-${g.group_id}`}
                >
                  {visible && <Check size={12} strokeWidth={3} />}
                </button>
                <Link
                  to="/groups"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 flex-1 min-w-0"
                  style={{ textDecoration: "none", color: "var(--text)" }}
                >
                  <span className="flex-1 min-w-0 truncate font-semibold">{g.name}</span>
                  {/* Smart nudge: if a hidden group has events in the
                      next 7 days, flag them so the user knows something
                      is coming and can re-enable if they want. */}
                  {personallyHidden && upcoming > 0 && (
                    <span
                      className="bd-chip flex items-center gap-1"
                      style={{
                        fontSize: "0.55rem",
                        padding: "0.1rem 0.35rem",
                        background: "color-mix(in srgb, var(--primary) 25%, transparent)",
                        borderColor: "var(--primary)",
                        color: "var(--primary)",
                      }}
                      title={`${upcoming} event${upcoming === 1 ? "" : "s"} in the next 7 days`}
                      data-testid={`my-groups-upcoming-${g.group_id}`}
                    >
                      {upcoming} this week
                    </span>
                  )}
                  {broadcastHidden && (
                    <span className="bd-chip flex items-center gap-1" style={{ fontSize: "0.55rem", padding: "0.1rem 0.35rem" }}>
                      <EyeOff size={9} /> Owner-hidden
                    </span>
                  )}
                  {isOwner && (
                    <span className="bd-chip flex items-center gap-1" style={{ fontSize: "0.55rem", padding: "0.1rem 0.35rem" }}>
                      <Crown size={9} /> Originator
                    </span>
                  )}
                </Link>
              </div>
            );
          })}

          {groups.length > 0 && (
            <Link
              to="/groups"
              onClick={() => setOpen(false)}
              className="block text-center text-xs px-3 py-2"
              style={{
                color: "var(--primary)",
                textDecoration: "none",
                // Sticky so it stays visible at the bottom of the
                // scrollable list — Steve reported on iPhone portrait
                // it could scroll off-screen with many groups.
                position: "sticky",
                bottom: "-0.4rem",
                marginTop: "0.2rem",
                background: "var(--bg-elev)",
                borderTop: "1px solid var(--border)",
                fontWeight: 600,
              }}
              data-testid="my-groups-manage-all"
            >
              Manage all groups →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
